import type { FileInfo } from '@sd/contracts';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { formatClock, formatDayDivider } from '../../format';
import { Icon } from '../../icons/Icon';
import { Button, IconButton } from '../../ui/controls';
import { dismissDialog, Modal } from '../../ui/overlays';
import { useDownload, usePreviewUrl } from '../attachments';
import { usePerson } from '../bits';
import { useAppEnv } from '../context';
import { clamp, fitted, onPicture, pinch, REST, swipeTarget, TAP_SCALE, zoomAt, type Point, type Size, type View } from '../viewerGeometry';

/** Space between two pictures while one slides in. */
const GAP = 24;
/** A pointer that moved less than this is a tap. */
const SLOP = 8;
const DOUBLE_TAP_MS = 280;
/** Pulled down this far (or flicked), the viewer closes. */
const DISMISS_PX = 120;

type Gesture =
  | { kind: 'pending'; start: Point; at: number; from: View; mouse: boolean }
  | { kind: 'pan'; start: Point; from: View }
  | { kind: 'swipe'; start: Point; at: number }
  | { kind: 'dismiss'; start: Point; at: number }
  | { kind: 'pinch'; from: View; mid: Point; distance: number }
  | { kind: 'done' };

/**
 * The photo viewer, as phone messengers have it: the pictures of the conversation side by side to swipe through, pinch
 * or double-tap to zoom, drag a zoomed picture, pull down to close. On a PC: arrows (buttons and keys), the wheel zooms
 * about the pointer, a click beside the picture closes. Tapping a picture on a touch screen hides and shows the bars.
 */
export function ImageViewer({ files, startId, onClose }: { files: FileInfo[]; startId: string; onClose(): void }) {
  const download = useDownload();
  const env = useAppEnv();
  // The picture is followed by its id: an older page loaded meanwhile puts pictures in front of it.
  const [currentId, setCurrentId] = useState(startId);
  const index = Math.max(0, files.findIndex((f) => f.id === currentId));
  const [bare, setBare] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const file = files[index]!;
  const uploader = usePerson(file.uploaderId);
  const root = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const view = useRef<View>(REST);
  const offset = useRef<Point>({ x: 0, y: 0 });
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture>({ kind: 'done' });
  const lastTap = useRef<{ at: number; point: Point } | null>(null);
  /** Two taps also make a browser's compatibility dblclick: only a mouse's double click zooms through onDoubleClick. */
  const lastPointer = useRef('');
  const tapTimer = useRef(0);
  const frame = useRef(0);
  const ease = useRef(false);
  const fade = useRef(1);
  const zoomedNow = useRef(false);
  const liveTimer = useRef(0);
  /**
   * Sizes read once per gesture (or picture, or window size), not at every move: reading layout right after writing a
   * transform made the browser lay out again at each finger move.
   */
  const geometry = useRef<{ box: Size; picture: Size; rect: DOMRect } | null>(null);
  const measure = useCallback(() => {
    if (!geometry.current && stage.current) {
      const rect = stage.current.getBoundingClientRect();
      const box = { w: rect.width, h: rect.height };
      const img = stage.current.querySelector<HTMLImageElement>('.pv-slide.on img');
      // The original's size from the file (the small copy shown first is drawn at it), else from the image once loaded.
      const natural = file.width && file.height ? { w: file.width, h: file.height } : { w: img?.naturalWidth ?? 0, h: img?.naturalHeight ?? 0 };
      const measured = { box, picture: fitted(natural, box), rect };
      // Kept only once the picture's own size is known (loaded, or given by the server).
      if (natural.w) geometry.current = measured;
      return measured;
    }
    return geometry.current ?? { box: { w: 0, h: 0 }, picture: { w: 0, h: 0 }, rect: new DOMRect() };
  }, [file.width, file.height]);
  const box = useCallback((): Size => measure().box, [measure]);
  const picture = useCallback((): Size => measure().picture, [measure]);
  /** A point of the screen relative to the stage centre. */
  const local = useCallback(
    (x: number, y: number): Point => {
      const { rect } = measure();
      return { x: x - rect.left - rect.width / 2, y: y - rect.top - rect.height / 2 };
    },
    [measure],
  );
  useEffect(() => {
    const stale = () => (geometry.current = null);
    window.addEventListener('resize', stale);
    return () => window.removeEventListener('resize', stale);
  }, []);

  /**
   * Writes the zoom and the slide offset. The track is always a layer of its own and the picture is one while it moves
   * (messenger CSS .pv-live), so a move is the compositor's work, not a repaint of a large photo; once the picture rests
   * it leaves its layer and is drawn sharp at its new scale.
   */
  const paint = useCallback(() => {
    frame.current = 0;
    const animate = ease.current;
    ease.current = false;
    const img = stage.current?.querySelector<HTMLElement>('.pv-slide.on .pv-pic');
    const v = view.current;
    if (img) {
      img.classList.add('pv-live');
      img.style.transition = animate ? 'transform .26s var(--ease)' : 'none';
      img.style.transform = v.scale === 1 && !v.x && !v.y ? '' : `translate3d(${v.x}px, ${v.y}px, 0) scale(${v.scale})`;
      window.clearTimeout(liveTimer.current);
      if (gesture.current.kind === 'done') liveTimer.current = window.setTimeout(() => img.classList.remove('pv-live'), animate ? 320 : 160);
    }
    const t = track.current;
    if (t) {
      t.style.transition = animate ? 'transform .32s var(--ease)' : 'none';
      t.style.transform = `translate3d(calc(${-index} * (100% + ${GAP}px) + ${offset.current.x}px), ${offset.current.y}px, 0)`;
    }
    // Pulling down fades the dark backdrop with it.
    const f = Math.max(0, 1 - Math.abs(offset.current.y) / 400);
    if (f !== fade.current) {
      fade.current = f;
      root.current?.style.setProperty('--pv-fade', String(f));
    }
    const z = v.scale > 1.01;
    if (z !== zoomedNow.current) {
      zoomedNow.current = z;
      setZoomed(z);
    }
  }, [index]);
  /** Asks for a paint at the next frame: a finger moves more often than the screen draws (120 Hz touch, 60 Hz screen). */
  const draw = useCallback(
    (animate: boolean) => {
      if (animate) ease.current = true;
      if (!frame.current) frame.current = window.requestAnimationFrame(paint);
    },
    [paint],
  );
  useEffect(
    () => () => {
      window.cancelAnimationFrame(frame.current);
      window.clearTimeout(liveTimer.current);
    },
    [],
  );
  const setView = useCallback(
    (next: View, animate: boolean) => {
      view.current = next;
      draw(animate);
    },
    [draw],
  );

  // Another picture: it starts fitted and the slide eases there; the one left keeps no zoom. The same picture at
  // another place in the list (a page loaded in front of it) is only put back under the stage.
  const shown = useRef(currentId);
  useLayoutEffect(() => {
    const moved = shown.current !== currentId;
    shown.current = currentId;
    geometry.current = null;
    if (moved) {
      view.current = REST;
      offset.current = { x: 0, y: 0 };
      stage.current?.querySelectorAll<HTMLElement>('.pv-pic').forEach((img) => {
        img.style.transform = '';
        img.classList.remove('pv-live');
      });
    }
    // At once, not at the next frame: opened at a later picture, the track must not show its start for a frame.
    window.cancelAnimationFrame(frame.current);
    ease.current = moved;
    paint();
  }, [currentId, index, paint]);

  const go = useCallback((next: number) => setCurrentId(files[Math.min(files.length - 1, Math.max(0, next))]!.id), [files]);
  const zoomBy = useCallback(
    (factor: number, at: Point = { x: 0, y: 0 }) => setView(zoomAt(view.current, view.current.scale * factor, at, picture(), box()), true),
    [setView, picture, box],
  );
  const toggleZoom = useCallback(
    (at: Point) => setView(view.current.scale > 1.01 ? REST : zoomAt(view.current, TAP_SCALE, at, picture(), box()), true),
    [setView, picture, box],
  );

  // Keys: ← → for the pictures, + − 0 for the zoom (Escape is the dialog's). Heard on the document: the arrow button
  // pressed last goes away at the first or last picture and the focus with it. Not while another dialog is on top.
  useEffect(() => {
    const layer = root.current?.closest<HTMLElement>('.modal');
    if (!layer) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
      const layers = document.querySelectorAll('.modal');
      if (layers[layers.length - 1] !== layer) return;
      const action = { ArrowLeft: () => go(index - 1), ArrowRight: () => go(index + 1), '+': () => zoomBy(1.5), '=': () => zoomBy(1.5), '-': () => zoomBy(1 / 1.5), '0': () => setView(REST, true) }[event.key];
      if (!action) return;
      event.preventDefault();
      action();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, index, zoomBy, setView]);

  // The wheel (and a trackpad pinch, which arrives as a wheel with ctrl) zooms about the pointer.
  useEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0015));
      setView(zoomAt(view.current, view.current.scale * factor, local(event.clientX, event.clientY), picture(), box()), false);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setView, local, picture, box]);
  useEffect(() => () => window.clearTimeout(tapTimer.current), []);

  const spread = () => {
    const [a, b] = [...pointers.current.values()];
    return { mid: local((a!.x + b!.x) / 2, (a!.y + b!.y) / 2), distance: Math.hypot(a!.x - b!.x, a!.y - b!.y) };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest('button')) return;
    lastPointer.current = event.pointerType;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    // Fresh sizes for this gesture (the image may have loaded since).
    if (pointers.current.size === 1) geometry.current = null;
    if (pointers.current.size === 2) {
      // A second finger: whatever the first one did becomes a pinch from here.
      offset.current = { x: 0, y: 0 };
      gesture.current = { kind: 'pinch', from: view.current, ...spread() };
      draw(true);
    } else if (pointers.current.size === 1) {
      gesture.current = { kind: 'pending', start: { x: event.clientX, y: event.clientY }, at: event.timeStamp, from: view.current, mouse: event.pointerType === 'mouse' };
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    let g = gesture.current;
    if (g.kind === 'pinch') {
      if (pointers.current.size === 2) setView(pinch(g.from, g, spread(), picture(), box()), false);
      return;
    }
    if (g.kind === 'done') return;
    const dx = event.clientX - g.start.x;
    const dy = event.clientY - g.start.y;
    if (g.kind === 'pending') {
      if (Math.hypot(dx, dy) < SLOP) return;
      // Moving: a zoomed picture pans; otherwise sideways goes to the next picture and, by finger, down closes.
      if (g.from.scale > 1.01) g = { kind: 'pan', start: g.start, from: g.from };
      else if (Math.abs(dx) >= Math.abs(dy) || g.mouse) g = { kind: 'swipe', start: g.start, at: g.at };
      else g = { kind: 'dismiss', start: g.start, at: g.at };
      gesture.current = g;
    }
    if (g.kind === 'pan') {
      setView(clamp({ scale: g.from.scale, x: g.from.x + dx, y: g.from.y + dy }, picture(), box()), false);
    } else if (g.kind === 'swipe') {
      // Past the first or the last picture the slide only gives a little.
      const edge = (index === 0 && dx > 0) || (index === files.length - 1 && dx < 0);
      offset.current = { x: edge ? dx / 3 : dx, y: 0 };
      draw(false);
    } else if (g.kind === 'dismiss') {
      offset.current = { x: 0, y: Math.max(0, dy) };
      draw(false);
    }
  };

  const tap = (event: ReactPointerEvent<HTMLDivElement>, mouse: boolean) => {
    const point = local(event.clientX, event.clientY);
    const hit = onPicture(view.current, picture(), point);
    if (mouse) {
      // A click beside the picture closes; a double click on it zooms (onDoubleClick).
      if (!hit) dismissDialog(onClose);
      return;
    }
    const last = lastTap.current;
    if (last && event.timeStamp - last.at < DOUBLE_TAP_MS && Math.hypot(point.x - last.point.x, point.y - last.point.y) < 40) {
      window.clearTimeout(tapTimer.current);
      lastTap.current = null;
      toggleZoom(point);
      return;
    }
    lastTap.current = { at: event.timeStamp, point };
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => setBare((b) => !b), DOUBLE_TAP_MS);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.delete(event.pointerId)) return;
    const g = gesture.current;
    if (g.kind === 'pinch') {
      // The other finger stays down without starting anything until it lifts.
      gesture.current = { kind: 'done' };
      setView(clamp(view.current, picture(), box()), true);
      return;
    }
    if (pointers.current.size) return;
    gesture.current = { kind: 'done' };
    if (event.type === 'pointercancel') {
      offset.current = { x: 0, y: 0 };
      draw(true);
      return;
    }
    const dx = event.clientX - ('start' in g ? g.start.x : event.clientX);
    const dy = event.clientY - ('start' in g ? g.start.y : event.clientY);
    const elapsed = 'at' in g ? Math.max(1, event.timeStamp - g.at) : 1;
    if (g.kind === 'pending') tap(event, g.mouse);
    else if (g.kind === 'swipe') {
      const next = swipeTarget(index, files.length, dx, dx / elapsed, box().w);
      offset.current = { x: 0, y: 0 };
      if (next !== index) go(next);
      else draw(true);
    } else if (g.kind === 'dismiss') {
      if (dy > DISMISS_PX || (dy > 30 && dy / elapsed > 0.6)) dismissDialog(onClose);
      else {
        offset.current = { x: 0, y: 0 };
        draw(true);
      }
    }
  };

  // Two on each side are there before they are swiped to: one more than the next, so a quick second swipe finds its
  // picture ready instead of a blank that fills in.
  const neighbours = [index - 2, index - 1, index, index + 1, index + 2].filter((k) => k >= 0 && k < files.length);
  const when = `${formatDayDivider(file.createdAt, env.now())} ${formatClock(file.createdAt)}`;
  return (
    <Modal open onClose={onClose} labelledBy="pv-t" id="m-preview" className="viewer" dialogClassName="pv">
      <div ref={root} className={`pv-root${bare ? ' bare' : ''}`}>
        <div className="pv-top">
          <div className="pv-title">
            <h2 id="pv-t" className="ell">
              {uploader?.displayName ?? file.name}
            </h2>
            <span className="ell">
              {files.length > 1 ? (
                <b>
                  {index + 1} / {files.length}
                </b>
              ) : null}
              {when}
            </span>
          </div>
          <IconButton icon="zoomout" className="pv-zoom" label="축소" disabled={!zoomed} onClick={() => zoomBy(1 / 1.5)} />
          <IconButton icon="zoomin" className="pv-zoom" label="확대" onClick={() => zoomBy(1.5)} />
          <IconButton icon="x" label="닫기" onClick={() => dismissDialog(onClose)} />
        </div>
        <div
          ref={stage}
          className="pv-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={(event) => {
            if (lastPointer.current !== 'mouse') return;
            const point = local(event.clientX, event.clientY);
            if (onPicture(view.current, picture(), point)) toggleZoom(point);
          }}
        >
          <div ref={track} className="pv-track">
            {neighbours.map((k) => (
              <div key={files[k]!.id} className={`pv-slide${k === index ? ' on' : ''}`} style={{ transform: `translateX(calc(${k} * (100% + ${GAP}px)))` }} aria-hidden={k !== index}>
                <Slide file={files[k]!} current={k === index} />
              </div>
            ))}
          </div>
          {index > 0 ? <IconButton icon="back" className="pv-nav prev" label="이전 사진" onClick={() => go(index - 1)} /> : null}
          {index < files.length - 1 ? <IconButton icon="right" className="pv-nav next" label="다음 사진" onClick={() => go(index + 1)} /> : null}
        </div>
        <div className="pv-foot">
          <span className="pv-name ell">{file.name}</span>
          <Button variant="primary" icon="download" disabled={file.scan === 'infected'} onClick={() => void download(file)}>
            원본 저장
          </Button>
        </div>
        <span className="sr" aria-live="polite">
          {files.length > 1 ? `사진 ${index + 1}/${files.length}, ${file.name}` : ''}
        </span>
      </div>
    </Modal>
  );
}

/** A layer of the picture, shown only once decoded: it then appears in one frame, with nothing blank before it. */
function useDecoded(url: string | null): string | null {
  const [decoded, setDecoded] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    const image = new Image();
    image.src = url;
    void Promise.resolve(image.decode?.())
      .catch(() => {})
      .then(() => alive && setDecoded(url));
    return () => {
      alive = false;
    };
  }, [url]);
  return url && decoded === url ? url : null;
}

/**
 * One picture of the viewer: the tile's small copy (usually loaded already) under the screen-size copy, which covers
 * it once decoded, both drawn at the size the original has on this screen. Never the original: a 12 MP photo decoded
 * as it was swiped to showed blank, then small, then sharp. Zoom and pan move the whole stack (.pv-pic).
 */
function Slide({ file, current }: { file: FileInfo; current: boolean }) {
  const tile = useDecoded(usePreviewUrl(file, { first: current, size: 'tile' }));
  const screen = useDecoded(usePreviewUrl(file, { first: current, size: 'screen' }));
  // Nothing to show yet: a dark slide, and only after a moment a word about it (a quick load shows no flash of text).
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 400);
    return () => window.clearTimeout(timer);
  }, []);
  const size = file.width && file.height ? { width: `min(100%, ${file.width}px)`, height: `min(100%, ${file.height}px)` } : undefined;
  if (tile || screen)
    return (
      <div className="pv-pic">
        {tile ? <img className="pv-img" src={tile} alt={screen ? '' : file.name} draggable={false} style={size} /> : null}
        {screen ? <img className="pv-img" src={screen} alt={file.name} draggable={false} style={size} /> : null}
      </div>
    );
  if (!slow && file.scan === 'clean') return <div className="pv-pic" />;
  return (
    <span className="pv-wait">
      <Icon name={file.scan === 'clean' ? 'sync' : 'clock'} className={file.scan === 'clean' ? 'spin' : undefined} />
      {file.scan === 'clean' ? '불러오는 중…' : '검사가 끝나면 볼 수 있어요'}
    </span>
  );
}
